import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAvatar, removeAvatar, uploadAvatar } from '@/apis/avatar';
import { getAvatarDisplayUrl, normalizeAvatarStatus } from '@/utils/avatar';

const AVATAR_STALE_TIME_MS = 120_000;
const AVATAR_POLLING_TIMEOUT_MS = 60_000;
const AVATAR_FAST_POLL_WINDOW_MS = 20_000;
const AVATAR_FAST_POLL_INTERVAL_MS = 2_000;
const AVATAR_SLOW_POLL_INTERVAL_MS = 5_000;

type UseUserAvatarOptions = {
  username?: string | null;
  enabled?: boolean;
  size?: '48' | '128' | '256';
};

export const getUserAvatarQueryKey = (username?: string | null) => ['user-avatar', username];

export const useUserAvatar = ({
  username,
  enabled = true,
  size = '256',
}: UseUserAvatarOptions = {}) => {
  const queryClient = useQueryClient();

  const avatarQuery = useQuery({
    queryKey: getUserAvatarQueryKey(username),
    enabled: enabled && Boolean(username),
    queryFn: () => fetchAvatar(username!),
    staleTime: AVATAR_STALE_TIME_MS,
    refetchInterval: (query) => {
      const avatar = query.state.data;
      if (!avatar || normalizeAvatarStatus(avatar.status) !== 'PROCESSING') {
        return false;
      }

      const createdAtMs = avatar.created_at ? new Date(avatar.created_at).getTime() : Date.now();
      const elapsedMs = Date.now() - createdAtMs;

      if (elapsedMs >= AVATAR_POLLING_TIMEOUT_MS) {
        return false;
      }

      return elapsedMs < AVATAR_FAST_POLL_WINDOW_MS
        ? AVATAR_FAST_POLL_INTERVAL_MS
        : AVATAR_SLOW_POLL_INTERVAL_MS;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: Blob | File) => uploadAvatar(username!, file),
    onSuccess: (avatar) => {
      queryClient.setQueryData(getUserAvatarQueryKey(username), avatar);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => removeAvatar(username!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getUserAvatarQueryKey(username) });
    },
  });

  return {
    ...avatarQuery,
    avatar: avatarQuery.data,
    avatarUrl: getAvatarDisplayUrl(avatarQuery.data, size),
    uploadAvatar: uploadMutation.mutateAsync,
    isUploadingAvatar: uploadMutation.isPending,
    removeAvatar: removeMutation.mutateAsync,
    isRemovingAvatar: removeMutation.isPending,
  };
};
